/**
 * A photo is made smaller in the browser before it is sent (owner, 4 October 2026). The largest picture the app
 * shows is 1280 px wide (08 media), so a photo keeps at most this many pixels on its long side: twice that width
 * for a photo taken upright. A phone's 3840 × 5120 photo then weighs about a megabyte instead of several and is
 * sent in seconds instead of a minute or more. The full-size original is not kept anywhere.
 */
export const PHOTO_LONG_SIDE = 2560;
/** The kinds that are fitted, with the quality they are written at. A GIF moves and is sent as it is; not every browser can write WebP. */
const WRITTEN: Readonly<Record<string, number | undefined>> = { 'image/jpeg': 0.85, 'image/png': undefined };

/** The size a picture of `width` × `height` is sent with, or null when it is small enough already (or is no picture). */
export function fittedSize(width: number, height: number, longSide = PHOTO_LONG_SIDE): { width: number; height: number } | null {
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width < 1 || height < 1) return null;
  const long = Math.max(width, height);
  if (long <= longSide) return null;
  const scale = longSide / long;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * The same photo, smaller, when it is larger than the app can show; otherwise the file itself. The browser's own
 * image element reads it, so a photo stored sideways with a rotation tag comes out upright, as it is shown.
 * Anything unexpected (no canvas, a picture the browser cannot read) leaves the file as it was.
 */
export async function fitPhoto(file: File): Promise<File> {
  const kind = file.type.toLowerCase();
  if (!(kind in WRITTEN) || typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return file;
  const address = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((loaded, failed) => { image.onload = () => loaded(); image.onerror = () => failed(new Error('unreadable')); image.src = address; });
    const size = fittedSize(image.naturalWidth, image.naturalHeight);
    if (!size) return file;
    const canvas = document.createElement('canvas');
    canvas.width = size.width; canvas.height = size.height;
    const pen = canvas.getContext('2d');
    if (!pen) return file;
    pen.imageSmoothingQuality = 'high';
    pen.drawImage(image, 0, 0, size.width, size.height);
    const written = await new Promise<Blob | null>((made) => { canvas.toBlob(made, kind, WRITTEN[kind]); });
    // Hands the pixels back at once: a phone keeps little canvas memory.
    canvas.width = 0; canvas.height = 0;
    // A browser that cannot write this kind answers with another one; then, and when nothing was gained, the picked file is sent.
    if (!written || written.type !== kind || written.size >= file.size) return file;
    return new File([written], file.name, { type: kind, lastModified: file.lastModified });
  } catch { return file; }
  finally { URL.revokeObjectURL(address); }
}
